import { Paper } from '@mui/material';
import { KpiCard } from './KpiCard';
import { DashboardLineChart } from './DashboardLineChart';
import { DashboardBarChart } from './DashboardBarChart';
import { DashboardTable } from './DashboardTable';
import { TaskInboxWidget } from './TaskInboxWidget';
import type { WidgetConfig } from '@/hooks/dashboard/useDashboardWidgets';

export interface WidgetDataMap {
  [widgetId: string]: {
    data?: unknown;
    loading?: boolean;
  };
}

export interface DashboardWidgetRendererProps {
  widget: WidgetConfig;
  data?: unknown;
  loading?: boolean;
}

export function DashboardWidgetRenderer({ widget, data, loading }: DashboardWidgetRendererProps) {
  switch (widget.type) {
    case 'kpiCard': {
      const kpiData = data as { current: number; comparison?: number; delta?: number } | undefined;
      return (
        <KpiCard
          displayName={widget.displayName}
          current={kpiData?.current ?? 0}
          comparison={kpiData?.comparison}
          delta={kpiData?.delta}
          unitType={widget.display?.unitType as string | undefined}
          unitLabel={widget.display?.unitLabel as string | undefined}
          decimals={widget.display?.decimals as number | undefined}
          compact={widget.display?.compact as boolean | undefined}
          loading={loading}
        />
      );
    }

    case 'lineChart': {
      const chartData = (data as Record<string, unknown>[]) ?? [];
      return (
        <Paper sx={{ p: 2, height: '100%' }}>
          <DashboardLineChart
            displayName={widget.displayName}
            data={chartData}
            dimension={widget.dimension ?? 'date'}
            measures={widget.measures ?? []}
            loading={loading}
          />
        </Paper>
      );
    }

    case 'barChart': {
      const barData = (data as Record<string, unknown>[]) ?? [];
      return (
        <Paper sx={{ p: 2, height: '100%' }}>
          <DashboardBarChart
            displayName={widget.displayName}
            data={barData}
            measure={widget.measure ?? ''}
            groupBy={widget.groupBy ?? ''}
            loading={loading}
          />
        </Paper>
      );
    }

    case 'table': {
      const tableData = (data as Record<string, unknown>[]) ?? [];
      return (
        <Paper sx={{ p: 2, height: '100%' }}>
          <DashboardTable
            displayName={widget.displayName}
            data={tableData}
            columns={widget.columns ?? []}
            sortBy={widget.sortBy}
            sortDirection={widget.sortDirection as 'asc' | 'desc' | undefined}
            limit={widget.limit}
            loading={loading}
          />
        </Paper>
      );
    }

    case 'taskInbox': {
      return <TaskInboxWidget limit={(widget.limit as number) ?? 5} />;
    }

    default:
      return (
        <Paper sx={{ p: 2, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          Unknown widget type: {widget.type}
        </Paper>
      );
  }
}
